import { getSupabaseBrowserClient } from '../../../lib/supabase/client'
import type { OrganizationSummary } from '../../organizations/lib/types'
import { seedDemoApprovalOverview } from '../lib/demoState'
import type { ApprovalItem, ApprovalOverview } from '../lib/types'

async function getAssigneeLookup(organizationId: string) {
  const supabase = getSupabaseBrowserClient()
  if (!supabase) {
    return new Map<string, string>()
  }

  const { data } = await supabase
    .from('organization_members')
    .select('user_id, role')
    .eq('organization_id', organizationId)
    .eq('status', 'active')

  const map = new Map<string, string>()

  for (const row of (data ?? []) as Array<Record<string, unknown>>) {
    const userId = row.user_id ? String(row.user_id) : null
    const role = String(row.role ?? '')
    const label =
      role === 'owner'
        ? 'Inhaber'
        : role === 'admin'
          ? 'Admin'
          : role === 'approver'
            ? 'Freigabe'
            : 'Teammitglied'

    if (userId) {
      map.set(userId, label)
    }
  }

  return map
}

function mapRowsToOverview(rows: Array<Record<string, unknown>>, assigneeLookup: Map<string, string>): ApprovalOverview {
  const items: ApprovalItem[] = rows.map((row) => {
    const content = (row.content_items ?? {}) as Record<string, unknown>
    const requestedFromUserId = row.requested_from_user_id ? String(row.requested_from_user_id) : null

    return {
      id: String(row.id),
      contentItemId: row.content_item_id ? String(row.content_item_id) : null,
      title: String(content.title ?? 'Unbenannter Inhalt'),
      channel: String(content.channel ?? 'Unbekannt'),
      contentType: (content.type as ApprovalItem['contentType']) ?? 'social_post',
      status: (row.status as ApprovalItem['status']) ?? 'pending',
      contentStatus: (content.status as ApprovalItem['contentStatus']) ?? 'draft',
      scheduledFor: (content.scheduled_for as string | null) ?? null,
      dueAt: (row.due_at as string | null) ?? null,
      comment: (row.comment as string | null) ?? null,
      requestedFromUserId,
      assigneeLabel: requestedFromUserId ? assigneeLookup.get(requestedFromUserId) ?? 'Teammitglied' : 'Nicht zugewiesen',
    }
  })

  const now = Date.now()
  const overdueCount = items.filter(
    (item) => item.status === 'pending' && item.dueAt && new Date(item.dueAt).getTime() < now,
  ).length

  return {
    pendingCount: items.filter((item) => item.status === 'pending').length,
    approvedCount: items.filter((item) => item.status === 'approved').length,
    changesRequestedCount: items.filter((item) => item.status === 'changes_requested').length,
    overdueCount,
    items,
  }
}

export async function getApprovalOverview(organization: OrganizationSummary | null): Promise<ApprovalOverview> {
  const supabase = getSupabaseBrowserClient()

  if (!supabase || organization?.source === 'local') {
    return seedDemoApprovalOverview(organization)
  }

  if (!organization) {
    return {
      pendingCount: 0,
      approvedCount: 0,
      changesRequestedCount: 0,
      overdueCount: 0,
      items: [],
    }
  }

  const { data, error } = await supabase
    .from('approval_requests')
    .select(
      `
        id,
        content_item_id,
        status,
        due_at,
        comment,
        requested_from_user_id,
        content_items (
          title,
          channel,
          type,
          status,
          scheduled_for
        )
      `,
    )
    .eq('organization_id', organization.id)
    .order('created_at', { ascending: false })
    .limit(12)

  if (error || !data) {
    return {
      pendingCount: 0,
      approvedCount: 0,
      changesRequestedCount: 0,
      overdueCount: 0,
      items: [],
    }
  }

  const assigneeLookup = await getAssigneeLookup(organization.id)

  return mapRowsToOverview(data as Array<Record<string, unknown>>, assigneeLookup)
}